import dayjs from 'dayjs';
import { FormEvent, useEffect, useRef, useState } from 'react';

const FMT = 'YYYY-MM-DD';
/** the earliest date taken as a real one, so a half-typed year (0002) is not mistaken for a date */
const EARLIEST = '1990-01-01';
const real = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v, FMT, true).isValid() && v >= EARLIEST;

/** Why a pair of dates cannot be used yet, or null when it can. */
export function rangeProblem(from: string, to: string, max: string): string | null {
  if (!from || !to) return null;
  if (!real(from) || !real(to)) return 'Enter real dates.';
  if (from > max || to > max) return 'Dates cannot be after today.';
  if (from > to) return 'The To date is before the From date.';
  return null;
}

interface Props {
  from: string;
  to: string;
  /** the latest date that can be chosen (today) */
  max: string;
  onApply: (from: string, to: string) => void;
  onClose: () => void;
}

/**
 * A From date and a To date, for a range that is none of Day, Week, Month or Year. Apply stays off until both are real
 * dates in order; Escape, Cancel or a click outside closes it without changing anything.
 */
export function CustomRange({ from, to, max, onApply, onClose }: Props) {
  const ref = useRef<HTMLFormElement>(null);
  const [a, setA] = useState(from);
  const [b, setB] = useState(to);
  const problem = rangeProblem(a, b, max);
  const ready = !!a && !!b && !problem;
  const days = ready ? dayjs(b).diff(dayjs(a), 'day') + 1 : 0;

  useEffect(() => {
    const away = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      // the Custom button toggles this panel itself
      if (t.closest('[data-custom-toggle]')) return;
      if (ref.current && !ref.current.parentElement?.contains(t)) onClose();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    ref.current?.querySelector<HTMLInputElement>('input')?.focus();
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) onApply(a, b);
  };

  return (
    <form className="pc cr" ref={ref} role="dialog" aria-label="Custom date range" onSubmit={submit}>
      <div className="cr-title">Custom range</div>
      <div className="cr-fields">
        <label>
          <span>From date</span>
          <input type="date" value={a} max={max} min={EARLIEST} aria-invalid={!!problem} onChange={(e) => setA(e.target.value)} />
        </label>
        <label>
          <span>To date</span>
          <input type="date" value={b} max={max} min={a && real(a) ? a : EARLIEST} aria-invalid={!!problem} onChange={(e) => setB(e.target.value)} />
        </label>
      </div>
      <p className={`cr-note${problem ? ' bad' : ''}`} role={problem ? 'alert' : undefined} aria-live="polite">
        {problem ?? (ready ? `${days} ${days === 1 ? 'day' : 'days'} selected` : 'Pick both dates. Both are included.')}
      </p>
      <div className="cr-foot">
        <button type="button" className="cr-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="cr-apply" disabled={!ready}>Apply</button>
      </div>
    </form>
  );
}
